import { GOOD_FROM } from './scoreBand'

/**
 * The transcript lock (owner rule, restated 2026-10-14): a lesson's sentence
 * text — with its translation, word notes and subtitle choice — stays hidden
 * on the lesson page until the learner has failed three tries of that
 * lesson; after that every take of the lesson earns only part of its XP.
 *
 * The rule is the server's (`lessons_detail.transcript`) and the Android
 * app's, pinned for all three by backend-nest/docs/transcript-lock-cases.json:
 * per lesson; a failed try is a take the service refused (`failed`) or one
 * scored below the "good" band on its rounded score; a take still waiting is
 * no verdict; the text opens at three and never closes again.
 */
export const TRANSCRIPT_UNLOCK_TRIES = 3

export function isFailedTry(attempt, goodFrom = GOOD_FROM) {
  if (attempt?.status === 'failed') return true
  if (attempt?.status === 'scored') return attempt.overall !== null && attempt.overall !== undefined && Math.round(attempt.overall) < goodFrom
  return false
}

/** The lock from the learner's takes of one lesson (the attempt list's rows). */
export function transcriptLock(attempts, goodFrom = GOOD_FROM) {
  const failedTries = (attempts ?? []).filter((attempt) => isFailedTry(attempt, goodFrom)).length
  return { failedTries, unlocked: failedTries >= TRANSCRIPT_UNLOCK_TRIES }
}

/**
 * What the page shows, from the server's answer at load, the history re-read
 * after each send (either may be missing), and the practice on screen
 * (`local`: `{ wrongReps, unlocked }` — owner rule 2026-10-14: three
 * repetitions the live text did not match to the sentence open the text too,
 * and once open it stays open for the lesson, `unlocked` being that kept
 * memory). The text never closes again, so everything is merged upwards: the
 * attempt list is capped at the newest 50 and may count fewer than the
 * server does, never more.
 */
export function lockOf(server, fromHistory, local = null) {
  const known = [server, fromHistory].filter(Boolean)
  const wrongReps = Math.max(0, local?.wrongReps ?? 0)
  const localOpen = local?.unlocked === true || wrongReps >= TRANSCRIPT_UNLOCK_TRIES
  if (known.length === 0 && !localOpen && wrongReps === 0) {
    return { known: false, failedTries: 0, unlocked: false, openedLocally: false, unlockAfter: TRANSCRIPT_UNLOCK_TRIES, xpFactor: 1 }
  }
  const failedTries = Math.max(wrongReps, ...known.map((lock) => lock.failedTries ?? 0))
  return {
    known: known.length > 0 || localOpen,
    failedTries,
    unlocked: localOpen || known.some((lock) => lock.unlocked === true) || failedTries >= TRANSCRIPT_UNLOCK_TRIES,
    // Opened by the device's own rule — what `attempts_submit` is told (`transcriptOpenedLocally`).
    openedLocally: localOpen,
    unlockAfter: server?.unlockAfter ?? TRANSCRIPT_UNLOCK_TRIES,
    // A server that sends no factor deducts nothing.
    xpFactor: typeof server?.xpFactor === 'number' ? server.xpFactor : 1,
  }
}

const unlockKey = (userId, lessonId) => `tooti.transcript.${userId}.${lessonId}`

function storageOf(storage) {
  if (storage !== undefined) return storage
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/** Whether this learner already opened this lesson's text by wrong repetitions (kept across visits). */
export function readLocalUnlock(userId, lessonId, storage) {
  const store = storageOf(storage)
  if (!userId || !lessonId || !store) return false
  try {
    return store.getItem(unlockKey(userId, lessonId)) === '1'
  } catch {
    return false
  }
}

/** Keep it open for this learner and lesson. Storage that refuses is not an error: open for this visit only. */
export function rememberLocalUnlock(userId, lessonId, storage) {
  const store = storageOf(storage)
  if (!userId || !lessonId || !store) return
  try {
    store.setItem(unlockKey(userId, lessonId), '1')
  } catch {
    // Private window or full storage.
  }
}

/** «تلاش N از ۳»: the tries so far, never past the count that opens the text. */
export function triesShown(lock) {
  return Math.min(lock.failedTries, lock.unlockAfter)
}

/** How a share of the XP is said: «نصف» for one half, a percentage otherwise; null when nothing is deducted. */
export function xpShare(factor) {
  if (typeof factor !== 'number' || factor >= 1) return null
  if (factor === 0.5) return { half: true }
  return { percent: Math.round(factor * 100) }
}
